import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, Trash2, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const KIND_LABELS = { listing: "Land", jv: "Joint Venture", business: "Business" };
const KIND_TARGETS = {
    listing: "/marketplace",
    jv: "/jv",
    business: "/businesses",
};

/**
 * SavedSearchesList — Dashboard widget showing all saved searches the user has
 * created, plus delete + "Run" links that re-apply the filters on the source page.
 */
export default function SavedSearchesList() {
    const [items, setItems] = useState(null);

    const refresh = async () => {
        try {
            const { data } = await api.get("/saved-searches");
            setItems(data.items || []);
        } catch (e) {
            setItems([]);
        }
    };

    useEffect(() => { refresh(); }, []);

    const remove = async (id) => {
        try {
            await api.delete(`/saved-searches/${id}`);
            toast.success("Saved search deleted");
            refresh();
        } catch (e) {
            toast.error("Could not delete");
        }
    };

    if (items === null) return null;
    if (items.length === 0) {
        return (
            <Card className="p-5 mb-6 border-dashed" data-testid="saved-searches-empty">
                <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-coral/10 grid place-items-center shrink-0">
                        <Bookmark className="h-5 w-5 text-coral" />
                    </div>
                    <div>
                        <div className="font-app font-semibold text-sm mb-1">No saved searches yet</div>
                        <p className="text-xs text-muted-foreground font-app">
                            On the Marketplace, Joint Venture Portal, or Businesses page, click <strong>Save search</strong> to pin your favourite filters here.
                        </p>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card className="p-5 mb-6" data-testid="saved-searches-card">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Bookmark className="h-5 w-5 text-coral" />
                    <h3 className="font-display text-lg font-bold">Saved searches</h3>
                </div>
                <span className="text-xs text-muted-foreground font-app tabular-nums">{items.length}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                {items.map((s) => (
                    <div key={s.id} data-testid={`saved-search-${s.id}`} className="group bg-secondary/40 hover:bg-secondary/60 rounded-lg p-3 border border-border transition">
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="font-app font-semibold text-sm truncate">{s.name}</div>
                            <span className="text-[10px] uppercase tracking-wider text-coral font-app font-bold shrink-0">
                                {KIND_LABELS[s.kind] || s.kind}
                            </span>
                        </div>
                        <div className="text-xs text-muted-foreground font-app mb-2.5 line-clamp-2">
                            {Object.entries(s.filters || {}).length === 0
                                ? "All listings"
                                : Object.entries(s.filters).map(([k, v]) => `${k}=${v}`).join(" · ")}
                        </div>
                        <div className="flex gap-2">
                            <Button asChild size="sm" variant="outline" className="flex-1 h-8 text-xs font-app">
                                <Link to={KIND_TARGETS[s.kind] || "/marketplace"}>
                                    <Search className="h-3 w-3 mr-1" /> Run
                                </Link>
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => remove(s.id)}
                                data-testid={`saved-search-delete-${s.id}`}
                                className="h-8 px-2 text-muted-foreground hover:text-coral"
                            >
                                <Trash2 className="h-3 w-3" />
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
}
